import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import { dateTimeLao, fullLAK, num, shortDate, shortId } from "../lib/format";
import type {
  ApiError,
  Attachment,
  DocumentRequest,
  DocumentRequestDetail,
  DocumentType,
  RefList,
  RequestStatus,
  VillageCertification,
} from "../lib/api";
import { getCurrentUserId } from "../lib/auth";
import { formatCoords, mapsUrl } from "../lib/services";
import {
  HISTORY_EVENT_LABEL_LO,
  STATUS_LABEL_LO,
  VILLAGE_CERT_LABEL_LO,
  awaitingVillage,
  formatAgoLo,
  statusBadgeClass,
  villageCertBadgeClass,
  villageOverdue,
} from "../lib/requestStatus";
import { VillageWaiveModal } from "../components/VillageWaiveModal";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  Download,
  ExternalLink,
  FileText,
  Inbox,
  Lock,
  MessageSquare,
  PenLine,
  X,
} from "../components/icons";
import { PERM, canClaimRequests, canSignDocuments, currentPosition, hasPerm, seesApplicantPII } from "../lib/permissions";

async function fetchDetail(id: string): Promise<DocumentRequestDetail> {
  const { data, error, response } = await api.GET("/v1/admin/requests/{id}", {
    params: { path: { id } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

async function fetchDocumentTypes(): Promise<DocumentType[]> {
  const { data, error, response } = await api.GET("/v1/document-types", {
    params: { query: { limit: 100 } },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

// รหัสแขวง/เมือง/บ้านเป็น id ล้วน — ตัวเดียวที่แปลงเป็นชื่อได้คือ endpoint อ้างอิงสาธารณะ
// พวกนี้เอง ไม่ต้องใส่ token (security: [] ใน openapi.yaml)
async function fetchProvinces() {
  // openapi-fetch (0.17) แก้ FetchResponse เป็น `never` เอาเองเวลา operation
  // ไม่มี response นอกจาก 200 เลย (listProvinces ไม่มี 4xx/5xx ประกาศไว้ต่างจาก
  // listDistricts/listVillages ที่มี 400) — cast กลับเป็นรูปที่ถูกต้องตาม runtime จริง
  const { data, error, response } = (await api.GET("/v1/reference/provinces")) as {
    data?: RefList;
    error?: ApiError;
    response: Response;
  };
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}
async function fetchDistricts(provinceId: string) {
  const { data, error, response } = await api.GET("/v1/reference/districts", {
    params: { query: { province_id: provinceId } },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}
async function fetchVillages(districtId: string) {
  const { data, error, response } = await api.GET("/v1/reference/villages", {
    params: { query: { district_id: districtId } },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

// ป้ายชื่อฟิลด์ของฟอร์ม A01 (ดู apps/web-mobile/src/routes/request-flow/flow-state.ts
// `toFormData()`) — ประเภทเอกสารอื่นที่ยังใช้ฟอร์ม generic ไม่มีคีย์พวกนี้ จึงตกไปที่
// fallback แสดงคีย์ดิบแทน ไม่ทำให้ข้อมูลหาย
const A01_FIELD_LABEL: Record<string, string> = {
  // วิธีรับเอกสารที่ผู้ขอเลือกตอนจ่าย — ເຈ້ຍ = เจ้าหน้าที่ต้องพิมพ์ใบจริงตามจำนวนฉบับที่จ่าย
  delivery: "ຮັບເອກະສານແບບ",
  id_doc_kind: "ເອກະສານຢືນຢັນຕົວຕົນ",
  full_name: "ຊື່ ແລະ ນາມສະກຸນ",
  id_number: "ເລກບັດປະຈຳຕົວ",
  date_of_birth: "ວັນເດືອນປີເກີດ",
  occupation: "ອາຊີບ",
  house_no: "ເຮືອນເລກທີ",
  nationality: "ສັນຊາດ",
  cluster: "ຄຸ້ມ",
  unit: "ໜ່ວຍ",
  household_reg_no: "ເລກທີສຳມະໂນຄົວ",
  household_reg_date: "ວັນທີຈົດສຳມະໂນຄົວ",
  household_reg_district: "ເມືອງທີ່ຈົດສຳມະໂນຄົວ",
  household_reg_province: "ແຂວງທີ່ຈົດສຳມະໂນຄົວ",
  father_name: "ຊື່ພໍ່",
  mother_name: "ຊື່ແມ່",
  parents_village: "ບ້ານຂອງພໍ່ແມ່",
  parents_district: "ເມືອງຂອງພໍ່ແມ່",
  parents_province: "ແຂວງຂອງພໍ່ແມ່",
  purpose: "ຈຸດປະສົງຂໍໃຊ້",
};
const ADDRESS_KEYS = new Set(["province_id", "district_id", "village_id"]);
// ชนิดที่แนบแทน slot ได้ (ປື້ມສຳມະໂນຄົວ/พาสปอร์ตแทนบัตร — domain.DefaultIDCardAlternatives) ไม่มีป้ายในรายการของชนิดเอกสาร
const ATTACHMENT_KIND_LABEL: Record<string, string> = {
  family_book: "ສຳເນົາປື້ມສຳມະໂນຄົວ",
  passport: "ສຳເນົາໜັງສືຜ່ານແດນ",
};
// พิกัดบ้านจากขั้น ປັກໝຸດທີ່ຢູ່ ของ wizard (web-mobile `LOCATION_KEYS`) — แสดงรวมเป็นแถวเดียวพร้อมลิงก์แผนที่
const LOCATION_KEYS = new Set(["home_lat", "home_lng", "home_accuracy_m", "home_location_source"]);

function homeLocation(
  fd: Record<string, unknown>,
): { lat: number; lng: number; accuracyM: number | null; fromMap: boolean } | null {
  const num = (k: string) => {
    const v = fd[k];
    if (typeof v !== "string" || v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const lat = num("home_lat");
  const lng = num("home_lng");
  if (lat === null || lng === null) return null;
  const fromMap = fd.home_location_source === "map";
  return { lat, lng, accuracyM: fromMap ? null : num("home_accuracy_m"), fromMap };
}

const DELIVERY_LABEL: Record<string, string> = {
  digital: "ດິຈິຕອນ (ດາວໂຫຼດໃນແອັບ)",
  paper: "ໃບເຈ້ຍ — ຮັບທີ່ຕາແສງ (ພິມຕາມຈຳນວນສະບັບທີ່ຈ່າຍ)",
};

// ชนิดเอกสารที่ผู้ร้องเลือกยืนยันตัวตนใน wizard (form_data `id_doc_kind`) — ไม่มี = บัตรตามเดิม
const ID_DOC_KIND_LABEL: Record<string, string> = {
  id_card: "ບັດປະຈຳຕົວ",
  family_book: "ປື້ມສຳມະໂນຄົວ",
  passport: "ໜັງສືຜ່ານແດນ",
};

function formValue(v: unknown, key?: string): string {
  if (key === "delivery" && typeof v === "string") return DELIVERY_LABEL[v] ?? v;
  if (key === "id_doc_kind" && typeof v === "string") return ID_DOC_KIND_LABEL[v] ?? v;
  if (v == null) return "—";
  if (typeof v === "string") return v.trim() || "—";
  return String(v);
}

const REJECT_CHIPS = ["ເອກະສານບໍ່ຄົບ", "ຂໍ້ມູນບໍ່ຕົງກັນ", "ບັດໝົດອາຍຸ"];
const MOREINFO_CHIPS = ["ກະລຸນາອັບໂຫລດເອກະສານໃໝ່", "ກະລຸນາຢືນຢັນທີ່ຢູ່", "ອື່ນໆ"];

type Panel = "approve" | "reject" | "moreinfo" | null;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function RequestDetailPage() {
  const { id = "" } = useParams();
  const me = getCurrentUserId();
  const canSign = canSignDocuments();
  // ผู้เซ็นที่ไม่ใช่ SuperAdmin = ຫົວໜ້າ/ຮອງຫົວໜ້າຕາແສງ — จอเน้นการลงนาม (mockup v2 t_chair)
  const signerFirst = canSign && currentPosition() !== "SA";
  const [searchParams] = useSearchParams();
  const fromSign = searchParams.get("from") === "sign";
  // ADR-0030: ปุ่มแต่ละขั้นตามรหัสสิทธิ์ (server เช็คซ้ำ) — APP-06 รับ · REV-02 อนุมัติ · REV-03 ส่งคืน · REV-04 ปฏิเสธ
  const canClaim = canClaimRequests();
  const canApprove = hasPerm(PERM.approve);
  const canReturn = hasPerm(PERM.returnForChanges);
  const canReject = hasPerm(PERM.reject);
  // ການເງິນເມືອງ/ກວດກາແຂວງ: API ບັງຂໍ້ມູນຜູ້ຂໍ ແລະ ບໍ່ໃຫ້ເປີດໄຟລ໌ (403) — ຈໍເບິ່ງໄດ້ຢ່າງດຽວ ບໍ່ມີປຸ່ມກະທຳ
  const handler = seesApplicantPII();
  const qc = useQueryClient();

  const detail = useQuery({ queryKey: ["requests", "detail", id], queryFn: () => fetchDetail(id), enabled: !!id });
  const types = useQuery({ queryKey: ["document-types"], queryFn: fetchDocumentTypes });
  // เวลาอ้างอิงของกำหนดนายบ้าน — คิดครั้งเดียวต่อการโหลดข้อมูล (ไม่เรียก Date.now() ระหว่าง render)
  const now = detail.dataUpdatedAt;

  const req = detail.data;
  // break-glass (ADR-0032): สิทธิ์ที่อนุมัติแล้วเปิดข้อมูล/ไฟล์ของคำร้องใบนี้แบบอ่านอย่างเดียว — ปุ่มกระทำยังใช้ `handler`
  const piiAccess = req?.pii_access ?? null;
  const piiOpen = !handler && piiAccess?.status === "active";
  const viewFull = handler || piiOpen;
  // คำขอที่รออนุมัติ: ถามสถานะใหม่เป็นระยะ (ยังไม่มีสิทธิ์ = การเปิดรายละเอียดไม่ถูกบันทึกเป็น pii_viewed)
  // · สิทธิ์ที่เปิดอยู่: โหลดใหม่ครั้งเดียวตอนหมดเวลา ให้จอกลับไปบังเอง (ไม่ poll — ทุกการเปิดถูก audit)
  const piiStatus = handler ? null : (piiAccess?.status ?? null);
  const piiExpiresAt = piiAccess?.expires_at ?? null;
  const refetchDetail = detail.refetch;
  useEffect(() => {
    if (piiStatus === "requested") {
      const t = window.setInterval(() => void refetchDetail(), 30_000);
      return () => window.clearInterval(t);
    }
    if (piiStatus === "active" && piiExpiresAt) {
      const wait = Math.max(0, new Date(piiExpiresAt).getTime() - Date.now()) + 1_000;
      const t = window.setTimeout(() => void refetchDetail(), Math.min(wait, 2_147_000_000));
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [piiStatus, piiExpiresAt, refetchDetail]);
  const formData = (req?.form_data ?? {}) as Record<string, unknown>;
  // ບົດບາດທີ່ເຫັນຂໍ້ມູນແບບບັງ ໄດ້ຄ່າທີ່ບັງແລ້ວ (ບໍ່ແມ່ນ UUID) — ຢ່າເອົາໄປຖາມຊື່ສະຖານທີ່
  const uuidOf = (v: unknown) => (typeof v === "string" && UUID_RE.test(v) ? v : undefined);
  const provinceId = uuidOf(formData.province_id);
  const districtId = uuidOf(formData.district_id);
  const villageId = uuidOf(formData.village_id);

  const provinces = useQuery({ queryKey: ["reference", "provinces"], queryFn: fetchProvinces, enabled: !!provinceId });
  const districts = useQuery({
    queryKey: ["reference", "districts", provinceId],
    queryFn: () => fetchDistricts(provinceId!),
    enabled: !!provinceId,
  });
  const villages = useQuery({
    queryKey: ["reference", "villages", districtId],
    queryFn: () => fetchVillages(districtId!),
    enabled: !!districtId,
  });

  const [panel, setPanel] = useState<Panel>(null);
  const [note, setNote] = useState("");
  const [openingAttachmentId, setOpeningAttachmentId] = useState<string | null>(null);
  // ຫົວໜ້າ (ຜູ້ເຊັນ) กดรับ/ตรวจแทนผู้ตรวจ — ซ่อนปุ่มของผู้ตรวจไว้จนกด (mockup ให้ຫົວໜ້າລົງນາມຢ່າງດຽວ)
  const [actForReviewer, setActForReviewer] = useState(false);
  const [viewing, setViewing] = useState<{ url: string; label: string; filename: string } | null>(null);
  const [waiving, setWaiving] = useState(false);

  const claim = useMutation<DocumentRequest, Error, void>({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/v1/admin/requests/{id}/transitions", {
        params: { path: { id } },
        body: { to: "in_review" },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["requests"] }),
  });

  const decide = useMutation<DocumentRequest, Error, { to: RequestStatus; note: string }>({
    mutationFn: async ({ to, note: n }) => {
      const { data, error, response } = await api.POST("/v1/admin/requests/{id}/transitions", {
        params: { path: { id } },
        body: { to, note: n.trim() || undefined },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests"] });
      setPanel(null);
      setNote("");
    },
  });

  const openIssuedDocument = useMutation<void, Error, void>({
    mutationFn: async () => {
      const { data, error, response } = await api.GET("/v1/requests/{id}/issued-document/url", {
        params: { path: { id } },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      window.open(data.url, "_blank", "noopener");
    },
  });

  const openAttachment = useMutation<void, Error, string>({
    mutationFn: async (attachmentId) => {
      setOpeningAttachmentId(attachmentId);
      const { data, error, response } = await api.GET("/v1/attachments/{id}/url", {
        params: { path: { id: attachmentId } },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      window.open(data.url, "_blank", "noopener");
    },
    onSettled: () => setOpeningAttachmentId(null),
  });

  if (detail.isPending) return <p className="body-sm">ກຳລັງໂຫຼດ…</p>;
  if (detail.isError || !req) {
    return (
      <div>
        <BackLink />
        <div className="alert-error" style={{ marginTop: "var(--space-4)" }}>
          ໂຫຼດຄຳຮ້ອງບໍ່ສຳເລັດ: {detail.error?.message ?? "ບໍ່ພົບຄຳຮ້ອງນີ້"}
        </div>
      </div>
    );
  }

  const type = types.data?.find((t) => t.id === req.document_type_id);
  const assetHistory = req.asset_history ?? null;
  const provinceName = provinces.data?.find((p) => p.id === provinceId)?.name_lo ?? provinceId;
  const districtName = districts.data?.find((d) => d.id === districtId)?.name_lo ?? districtId;
  const villageName = villages.data?.find((v) => v.id === villageId)?.name_lo ?? villageId;
  const hasAddress = provinceId || districtId || villageId;

  const infoRows = schemaInfoRows(formData, type);
  const attachmentLabel = (a: Attachment) =>
    type?.required_attachments.find((r) => r.kind === a.kind)?.label_lo ?? ATTACHMENT_KIND_LABEL[a.kind] ?? a.kind;
  const imageAttachments = req.attachments.filter((a) => a.mime_type.startsWith("image/"));
  // รูปที่แสดงเป็นภาพย่อแล้วไม่ต้องซ้ำเป็นแถว — ไฟล์อื่น (PDF ฯลฯ) ยังเป็นแถวพร้อมปุ่ม ເບິ່ງ
  const listedAttachments = handler ? req.attachments.filter((a) => !a.mime_type.startsWith("image/")) : req.attachments;

  const home = homeLocation(formData);
  const vc = req.village_certification;
  const villagePending = awaitingVillage(req);
  // ລໍນາຍບ້ານ ແລະ ຍັງບໍ່ເກີນກຳນົດ = ຮັບບໍ່ໄດ້ (server ຕອບ 422) — ເກີນແລ້ວຮັບໄດ້ ລະບົບຂ້າມຂັ້ນໃຫ້
  const claimBlockedByVillage = villagePending && !villageOverdue(vc, now);

  const assignedLabel =
    req.assigned_officer_id == null
      ? "ຍັງບໍ່ມີຄົນຮັບ"
      : req.assigned_officer_id === me
        ? "ທ່ານ"
        : req.assigned_officer_name || "ເຈົ້າໜ້າທີ່ທ່ານອື່ນ";

  const noteRequired = panel === "reject" || panel === "moreinfo";
  const canSubmitPanel = panel !== null && (!noteRequired || note.trim().length > 0);

  function openPanel(p: Exclude<Panel, null>) {
    setPanel(p);
    setNote("");
  }

  function submitPanel() {
    if (panel === "approve") decide.mutate({ to: "approved", note });
    else if (panel === "reject") decide.mutate({ to: "rejected", note });
    else if (panel === "moreinfo") decide.mutate({ to: "needs_changes", note });
  }

  // ---- ແຖບຂັ້ນຕອນ (mockup request__*) — ชำระเงินเกิดตอนยื่นในแอป จึงไม่มีขั้นแยก (ดูการ์ดค่าธรรมเนียม)
  const steps = buildSteps(req, villagePending);

  // ---- การ์ด ການດຳເນີນການ ตาม mockup v2 (work_queue.js actionCard) — ปุ่มตามสิทธิ์ (server เช็คซ้ำ)
  // ผู้ตรวจ (TK): ຮັບ → ຜ່ານການກວດ/ສົ່ງຄືນ · ຫົວໜ້າ (TH, ຜູ້ເຊັນ): ລົງນາມ/ສົ່ງຄືນ ตอนอนุมัติแล้ว
  // ຫົວໜ້າมีสิทธิ์รับ/ตรวจแบบ compat (ADR-0030) — ซ่อนไว้หลังปุ่ม "ກວດແທນຜູ້ກວດເອກະສານ" ไม่ให้ตาแสงที่ไม่มีผู้ตรวจติดขัด
  const reviewUnlocked = !signerFirst || actForReviewer;
  const canAct = handler && (canClaim || canApprove || canReturn || canReject || canSign);
  const returnPanel = (label: string) => (
    <>
      <div>
        <label className="form-field-label" htmlFor="decision-note">
          {panel === "approve" ? "ໝາຍເຫດ (ບໍ່ບັງຄັບ)" : panel === "reject" ? "ເຫດຜົນການປະຕິເສດ" : "ເຫດຜົນ — ສົ່ງໃຫ້ຜູ້ຂໍທາງແອັບ"}
          {noteRequired && <span className="required"> *</span>}
        </label>
        <textarea id="decision-note" className="input-field" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {panel !== "approve" && (
        <div className="row" style={{ flexWrap: "wrap" }}>
          {(panel === "reject" ? REJECT_CHIPS : MOREINFO_CHIPS).map((c) => (
            <button key={c} type="button" className="btn-outline btn-sm" onClick={() => setNote(c)}>
              {c}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        className={panel === "approve" ? "btn-primary rd-block" : "btn-danger rd-block"}
        disabled={!canSubmitPanel || decide.isPending}
        onClick={submitPanel}
      >
        {label}
      </button>
      <button type="button" className="btn-secondary rd-block" onClick={() => setPanel(null)}>
        ຍົກເລີກ
      </button>
      {decide.isError && <p className="field-error">{decide.error.message}</p>}
    </>
  );
  const actForReviewerLink = (
    <button type="button" className="btn-outline btn-sm rd-block" onClick={() => setActForReviewer(true)}>
      ກວດແທນຜູ້ກວດເອກະສານ
    </button>
  );

  let actionBody: ReactNode;
  if (!canAct) {
    // บทบาทที่ดูได้อย่างเดียว (ການເງິນ/ເມືອງ/ແຂວງ/ການເງິນຕາແສງ …) — mockup: lock note + สถานะที่รอ
    actionBody = (
      <>
        <div className="rd-note rd-note--muted">
          <Lock size={14} /> ບົດບາດນີ້ເບິ່ງໄດ້ຢ່າງດຽວ — ການກະທຳເຮັດໂດຍຜູ້ຮັບຜິດຊອບໃນຕາແສງ
        </div>
        <div className={req.status === "rejected" || req.status === "cancelled" ? "rd-note rd-note--error" : "rd-note"}>
          {waitingText(req, villagePending)}
        </div>
      </>
    );
  } else if (req.status === "submitted") {
    actionBody =
      canClaim && reviewUnlocked ? (
        <>
          <div className="rd-note">
            {claimBlockedByVillage
              ? "ລໍນາຍບ້ານຢັ້ງຢືນກ່ອນ — ຮັບໄດ້ເມື່ອນາຍບ້ານຢັ້ງຢືນ ຫຼື ເກີນກຳນົດ (ຫຼື ຂ້າມຂັ້ນນາຍບ້ານພ້ອມເຫດຜົນ)"
              : villagePending
                ? "ນາຍບ້ານເກີນກຳນົດແລ້ວ — ຮັບໄດ້ເລີຍ ລະບົບຈະຂ້າມຂັ້ນນາຍບ້ານໃຫ້ (ບັນທຶກໃນປະຫວັດ)"
                : "ຍັງບໍ່ມີຄົນຮັບ — ຮັບເຂົ້າຄິວຂອງທ່ານ ແລ້ວກວດຄວາມຄົບຖ້ວນ"}
          </div>
          <button
            type="button"
            className="btn-primary rd-block"
            disabled={claim.isPending || claimBlockedByVillage}
            onClick={() => claim.mutate()}
          >
            <Inbox size={16} /> ຮັບຄຳຮ້ອງນີ້
          </button>
          {claim.isError && <p className="field-error">{claim.error.message}</p>}
        </>
      ) : (
        <>
          <div className="rd-note">{waitingText(req, villagePending)}</div>
          {canClaim && !claimBlockedByVillage ? actForReviewerLink : null}
        </>
      );
  } else if (req.status === "in_review") {
    const mayReview = (canApprove || canReturn || canReject) && reviewUnlocked;
    actionBody = !mayReview ? (
      <>
        <div className="rd-note">{waitingText(req, villagePending)}</div>
        {canApprove || canReturn ? actForReviewerLink : null}
      </>
    ) : panel !== null ? (
      returnPanel(panel === "approve" ? "ຢືນຢັນ — ສົ່ງລໍລົງນາມ" : panel === "reject" ? "ຢືນຢັນປະຕິເສດ" : "ຢືນຢັນສົ່ງຄືນ")
    ) : (
      <>
        <div className="rd-note">ກວດຄວາມຄົບຖ້ວນ ກ່ອນສົ່ງໃຫ້ຫົວໜ້າລົງນາມ</div>
        {canApprove && (
          <button type="button" className="btn-primary rd-block" onClick={() => openPanel("approve")}>
            <Check size={16} /> ຜ່ານການກວດ — ສົ່ງລໍລົງນາມ
          </button>
        )}
        {canReturn && (
          <button type="button" className="btn-outline rd-block" onClick={() => openPanel("moreinfo")}>
            <MessageSquare size={16} /> ສົ່ງຄືນໃຫ້ແກ້ໄຂ
          </button>
        )}
        {canReject && (
          <button type="button" className="btn-outline btn-sm rd-block rd-danger-text" onClick={() => openPanel("reject")}>
            <X size={14} /> ປະຕິເສດຄຳຮ້ອງ
          </button>
        )}
      </>
    );
  } else if (req.status === "approved") {
    actionBody = !canSign ? (
      <div className="rd-note">{waitingText(req, villagePending)}</div>
    ) : panel === "moreinfo" ? (
      returnPanel("ຢືນຢັນສົ່ງຄືນ")
    ) : (
      <>
        <div className="rd-number">
          <div className="caption">ເລກທີ່ເອກະສານ</div>
          <div className="rd-number__value">ອອກອັດຕະໂນມັດເມື່ອລົງນາມ</div>
          <div className="caption">ຕາມລຳດັບ ບໍ່ຊ້ຳ ບໍ່ຂ້າມ · ເຊັນແລ້ວແກ້ເນື້ອໃນບໍ່ໄດ້</div>
        </div>
        <Link to={`/requests/sign?id=${encodeURIComponent(req.id)}`} className="btn-primary rd-block">
          <PenLine size={16} /> ລົງນາມ ແລະ ປະທັບຕາ
        </Link>
        {canReturn && (
          <button type="button" className="btn-outline rd-block" onClick={() => openPanel("moreinfo")}>
            <X size={16} /> ສົ່ງຄືນ
          </button>
        )}
      </>
    );
  } else if (req.status === "issued") {
    actionBody = (
      <>
        <div className="rd-note rd-note--success">
          ອອກເອກະສານແລ້ວ · ເລກທີ່ <span className="num" style={{ fontWeight: 600 }}>{req.document_number || "—"}</span>
        </div>
        {viewFull ? (
          <button
            type="button"
            className="btn-primary rd-block"
            disabled={openIssuedDocument.isPending}
            onClick={() => openIssuedDocument.mutate()}
          >
            <Download size={16} /> ດາວໂຫລດ PDF
          </button>
        ) : null}
        {openIssuedDocument.isError && <p className="field-error">{openIssuedDocument.error.message}</p>}
      </>
    );
  } else {
    actionBody = (
      <>
        <div className={req.status === "rejected" || req.status === "cancelled" ? "rd-note rd-note--error" : "rd-note"}>
          {waitingText(req, villagePending)}
        </div>
        {req.officer_note && req.status !== "draft" ? <p className="body-sm">{req.officer_note}</p> : null}
      </>
    );
  }

  return (
    <div>
      <header className="rd-header">
        <div>
          <h1 className="page-header__title">{type?.name_lo ?? req.document_type_id}</h1>
          <p className="page-header__subtitle">
            {type?.code} · <span className="num">#{shortId(req.id)}</span> · ຍື່ນ{" "}
            {req.submitted_at ? formatAgoLo(req.submitted_at) : "ຍັງບໍ່ໄດ້ຍື່ນ"}
          </p>
        </div>
        <div className="row" style={{ gap: "var(--space-3)" }}>
          <BackLink toSign={fromSign} />
          <span className={statusBadgeClass(req.status)}>{STATUS_LABEL_LO[req.status]}</span>
        </div>
      </header>

      <section className="card rd-steps-card">
        <ol className="rd-steps">
          {steps.map((s, i) => (
            <li key={s.label} className={`rd-step rd-step--${s.state}`}>
              <span className="rd-step__mark">
                {s.state === "done" ? <Check size={16} /> : s.state === "stopped" ? <X size={16} /> : <span className="num">{i + 1}</span>}
              </span>
              <span className="rd-step__label">{s.label}</span>
            </li>
          ))}
        </ol>
      </section>

      {vc?.status === "unrecognized" && (
        <div className="alert-error" role="alert" style={{ marginBottom: "var(--space-4)" }}>
          <AlertTriangle size={14} /> ນາຍບ້ານແຈ້ງວ່າບໍ່ຮູ້ຈັກຜູ້ຂໍໃນບ້ານ — ກວດສອບກ່ອນດຳເນີນການ
        </div>
      )}

      <div className="rd-layout">
        <div className="stack">
          <section className="card">
            <h2 className="section-title">ຂໍ້ມູນຄຳຮ້ອງ</h2>
            {!viewFull ? <p className="caption">ຂໍ້ມູນສ່ວນບຸກຄົນຖືກບັງ ສຳລັບບົດບາດນີ້</p> : null}
            <dl className="rd-kv">
              <dt>ຜູ້ຮັບຜິດຊອບ</dt>
              <dd>{assignedLabel}</dd>
              {req.requester_name ? (
                <>
                  <dt>ບັນຊີຜູ້ຍື່ນ</dt>
                  <dd>{req.requester_name}</dd>
                </>
              ) : null}
              {hasAddress ? (
                <>
                  <dt>ບ້ານ / ເມືອງ / ແຂວງ</dt>
                  <dd>{[villageName, districtName, provinceName].filter(Boolean).join(" · ") || "—"}</dd>
                </>
              ) : null}
              {infoRows.map((r) => (
                <Fragment key={r.label}>
                  <dt>{r.label}</dt>
                  <dd>{r.value}</dd>
                </Fragment>
              ))}
              {home ? (
                <>
                  <dt>ຕຳແໜ່ງເຮືອນ (ຈາກເຄື່ອງຜູ້ຮ້ອງ)</dt>
                  <dd>
                    <span className="num">{formatCoords(home.lat, home.lng, home.accuracyM)}</span>
                    <span className="caption"> · {home.fromMap ? "ຜູ້ຮ້ອງເລືອກເອງໃນແຜນທີ່" : "ຈາກ GPS ຂອງເຄື່ອງ"} · </span>
                    <a href={mapsUrl(home.lat, home.lng)} target="_blank" rel="noreferrer">
                      ເປີດແຜນທີ່
                    </a>
                  </dd>
                </>
              ) : null}
            </dl>
            {infoRows.length === 0 && !hasAddress && <p className="caption">ຍັງບໍ່ມີຂໍ້ມູນໃນແບບຟອມ</p>}
          </section>

          {assetHistory && (
            <section className="card">
              <h2 className="section-title">ປະຫວັດຊັບສິນ</h2>
              <div className="stack" style={{ marginTop: "var(--space-3)" }}>
                {assetHistory.seller_mismatch && (
                  <div className="alert-warning" role="alert">
                    <AlertTriangle size={14} /> ຜູ້ຂາຍບໍ່ກົງກັບເຈົ້າຂອງລ່າສຸດໃນລະບົບ — ກວດເອກະສານສິດກ່ອນອະນຸມັດ
                  </div>
                )}
                {assetHistory.in_progress && (
                  <div className="alert-warning" role="status">
                    <AlertTriangle size={14} /> ມີຄຳຮ້ອງອື່ນຂອງຊັບສິນນີ້ກຳລັງດຳເນີນຢູ່
                  </div>
                )}
                <dl className="rd-kv" style={{ marginTop: 0 }}>
                  <dt>{assetHistory.kind === "land" ? "ເລກທີໃບຕາດິນ" : "ເລກຖັງ"}</dt>
                  <dd className="num">{assetHistory.key}</dd>
                  <dt>ຈຳນວນການໂອນໃນລະບົບ</dt>
                  <dd className="num">{assetHistory.transfer_count}</dd>
                  {assetHistory.transfers.map((t, i) => (
                    <Fragment key={`${t.issued_at}-${String(i)}`}>
                      <dt className="num">{shortDate(t.issued_at)}</dt>
                      <dd>{[t.tasaeng_lo, t.village_lo].filter(Boolean).join(" · ") || "ບໍ່ລະບຸບ້ານ"}</dd>
                    </Fragment>
                  ))}
                </dl>
                <p className="caption">ສະເພາະທຸລະກຳທີ່ເຮັດຜ່ານລະບົບນີ້ — ບໍ່ແມ່ນທະບຽນທີ່ດິນ/ຂົນສົ່ງຂອງລັດ</p>
              </div>
            </section>
          )}

          <section className="card">
            <h2 className="section-title">ເອກະສານແນບ ({req.attachments.length})</h2>
            {/* รูปแสดงเป็นภาพย่อเลย — เฉพาะผู้เห็นข้อมูลเต็มตามสิทธิ์ (handler); คนที่เปิดด้วยคำขอ break-glass
                ยังกดเปิดทีละไฟล์ เพราะทุกการเปิดไฟล์ถูกบันทึกเป็นการเข้าดูข้อมูล (ADR-0032) */}
            {handler && imageAttachments.length > 0 ? (
              <div className="rd-thumbs">
                {imageAttachments.map((a) => (
                  <AttachmentThumb key={a.id} attachment={a} label={attachmentLabel(a)} onOpen={setViewing} />
                ))}
              </div>
            ) : null}
            <div className="rd-files">
              {req.attachments.length === 0 && <p className="caption">ບໍ່ມີເອກະສານຄັດຕິດ</p>}
              {listedAttachments.map((a) => {
                const label = attachmentLabel(a);
                return (
                  <div key={a.id} className="rd-file">
                    <FileText size={18} />
                    <div className="rd-file__body">
                      <div className="body-sm" style={{ fontWeight: 600 }}>
                        {label}
                      </div>
                      <div className="caption">
                        {a.filename} · <span className="num">{Math.round(a.size_bytes / 1024)} KB</span>
                      </div>
                    </div>
                    {viewFull ? (
                      <button
                        type="button"
                        className="btn-outline btn-sm"
                        disabled={openingAttachmentId === a.id}
                        onClick={() => openAttachment.mutate(a.id)}
                      >
                        <ExternalLink size={14} /> ເບິ່ງ
                      </button>
                    ) : (
                      <span className="caption">
                        <Lock size={12} /> ບັງໄວ້
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {openAttachment.isError && <p className="field-error">{openAttachment.error.message}</p>}
          </section>
        </div>
        {viewing ? <ImageLightbox image={viewing} onClose={() => setViewing(null)} /> : null}

        <div className="stack">
          <section className="card">
            <h2 className="section-title">ການດຳເນີນການ</h2>
            <div className="stack" style={{ marginTop: "var(--space-4)" }}>
              {actionBody}
            </div>
          </section>

          {vc && (
            <VillageCertCard
              cert={vc}
              pending={villagePending}
              overdue={villageOverdue(vc, now)}
              showSignature={viewFull}
              canWaive={handler && canClaim && villagePending}
              onWaive={() => setWaiving(true)}
            />
          )}

          <FeeCard requestId={req.id} type={type} />

          <section className="card">
            <h2 className="section-title">ປະຫວັດການປ່ຽນສະຖານະ</h2>
            {req.history.length === 0 ? (
              <p className="caption" style={{ marginTop: "var(--space-3)" }}>
                ຍັງບໍ່ມີການປ່ຽນສະຖານະ
              </p>
            ) : (
              <ol className="rd-timeline">
                {req.history
                  .slice()
                  .reverse()
                  .map((h, i) => (
                    <li key={h.id} className={i === 0 ? "rd-timeline__item is-latest" : "rd-timeline__item"}>
                      <div className="body-sm" style={{ fontWeight: 500 }}>
                        {h.event
                          ? HISTORY_EVENT_LABEL_LO[h.event]
                          : `${h.from_status ? STATUS_LABEL_LO[h.from_status] : "ເລີ່ມຕົ້ນ"} → ${STATUS_LABEL_LO[h.to_status]}`}
                        {h.actor_name ? <span className="caption"> · {h.actor_name}</span> : null}
                      </div>
                      {h.note ? <div className="caption">{h.note}</div> : null}
                      <div className="caption num">
                        {dateTimeLao(new Date(h.created_at).getTime())} · {formatAgoLo(h.created_at)}
                      </div>
                    </li>
                  ))}
              </ol>
            )}
          </section>
        </div>
      </div>
      {waiving && (
        <VillageWaiveModal
          requestId={req.id}
          villageName={villages.data?.find((v) => v.id === villageId)?.name_lo ?? null}
          onClose={() => setWaiving(false)}
        />
      )}
    </div>
  );
}


/**
 * แถวข้อมูลจาก form_data — ป้ายจาก `title` ใน form_schema ของชนิดนั้น (ผู้ดูแลตั้งที่จอ ຟອມກອກ) เรียงตาม
 * x-step/x-order ของ schema · คีย์ที่ schema ไม่รู้จักถอยไปป้ายของ A01 แล้วคีย์ดิบ (ข้อมูลไม่หาย)
 * ช่องระบบ (`x-system`) ที่ไม่มีค่าไม่แสดง · ที่อยู่/พิกัดแสดงแยก
 */
function schemaInfoRows(
  formData: Record<string, unknown>,
  type: DocumentType | undefined,
): { label: string; value: string }[] {
  const props = ((type?.form_schema as { properties?: Record<string, Record<string, unknown>> } | undefined)
    ?.properties ?? {}) as Record<string, Record<string, unknown>>;
  const keys = Object.keys(props);
  const order = (k: string) => {
    const p = props[k];
    if (!p) return 1_000_000;
    const o = typeof p["x-order"] === "number" ? p["x-order"] : 10_000 + keys.indexOf(k);
    return o;
  };
  return Object.entries(formData)
    .filter(([k, v]) => {
      if (ADDRESS_KEYS.has(k) || LOCATION_KEYS.has(k) || k === "for_self") return false;
      const p = props[k];
      if (p?.["x-system"] === true && (v == null || (typeof v === "string" && v.trim() === ""))) return false;
      return true;
    })
    .sort(([a], [b]) => order(a) - order(b))
    .map(([k, v]) => {
      const title = props[k]?.title;
      return { label: typeof title === "string" && title.trim() ? title : (A01_FIELD_LABEL[k] ?? k), value: formValue(v, k) };
    });
}

/** URL ชั่วคราวของไฟล์แนบ — presigned หมดอายุได้ จึงไม่ cache ข้ามการเปิดหน้า */
function useAttachmentURL(id: string) {
  return useQuery({
    queryKey: ["attachments", "url", id],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/v1/attachments/{id}/url", {
        params: { path: { id } },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data.url;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** ภาพย่อของไฟล์แนบที่เป็นรูป — กดแล้วขยายในหน้า */
function AttachmentThumb({
  attachment,
  label,
  onOpen,
}: {
  attachment: Attachment;
  label: string;
  onOpen: (img: { url: string; label: string; filename: string }) => void;
}) {
  const url = useAttachmentURL(attachment.id);
  return (
    <figure className="rd-thumb">
      <button
        type="button"
        className="rd-thumb__frame"
        disabled={!url.data}
        aria-label={`ຂະຫຍາຍ ${label}`}
        onClick={() => url.data && onOpen({ url: url.data, label, filename: attachment.filename })}
      >
        {url.data ? (
          <img src={url.data} alt={label} loading="lazy" />
        ) : url.isError ? (
          <span className="caption">ໂຫຼດຮູບບໍ່ໄດ້</span>
        ) : (
          <span className="caption">ກຳລັງໂຫຼດ…</span>
        )}
      </button>
      <figcaption>
        <div className="body-sm" style={{ fontWeight: 600 }}>
          {label}
        </div>
        <div className="caption rd-thumb__name" title={attachment.filename}>
          {attachment.filename} · <span className="num">{Math.round(attachment.size_bytes / 1024)} KB</span>
        </div>
      </figcaption>
    </figure>
  );
}

/** ดูรูปขนาดเต็มในหน้า + ลิงก์เปิดไฟล์จริงในแท็บใหม่ */
function ImageLightbox({
  image,
  onClose,
}: {
  image: { url: string; label: string; filename: string };
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={image.label} onClick={onClose}>
      <div className="modal rd-lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div style={{ minWidth: 0 }}>
            <h2 className="modal__title">{image.label}</h2>
            <p className="caption rd-thumb__name">{image.filename}</p>
          </div>
          <div className="row" style={{ gap: "var(--space-2)" }}>
            <a className="btn-outline btn-sm" href={image.url} target="_blank" rel="noreferrer">
              <ExternalLink size={14} /> ເປີດແຖບໃໝ່
            </a>
            <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="rd-lightbox__img">
          <img src={image.url} alt={image.label} />
        </div>
      </div>
    </div>
  );
}

type StepState = "done" | "current" | "todo" | "stopped";

/**
 * ขั้นของคำร้องสำหรับแถบบนสุด (mockup v2) — ຍື່ນ → [ນາຍບ້ານ] → ຜູ້ກວດເອກະສານ → ຫົວໜ້າລົງນາມ → ອອກເອກະສານ
 * ชำระเงินเกิดตอนยื่นในแอป (ไม่ใช่หลังลงนามแบบ mockup) จึงไม่มีขั้น ຊຳລະ — ดูการ์ดค่าธรรมเนียม
 * ขั้นนายบ้านแสดงเฉพาะคำร้องที่ใช้ขั้นนี้ (ADR-0031) · ปฏิเสธ/ยกเลิก = ขั้นที่หยุดเป็นสีแดง
 */
function buildSteps(req: DocumentRequestDetail, villagePending: boolean): { label: string; state: StepState }[] {
  const labels = ["ຍື່ນ"];
  if (req.village_certification) labels.push(req.village_certification.status === "waived" ? "ນາຍບ້ານ (ຂ້າມ)" : "ນາຍບ້ານຢັ້ງຢືນ");
  const review = labels.push("ຜູ້ກວດເອກະສານ") - 1;
  const sign = labels.push("ຫົວໜ້າລົງນາມ") - 1;
  labels.push("ອອກເອກະສານ");

  let current: number;
  let stopped = false;
  switch (req.status) {
    case "draft":
      current = 0;
      break;
    case "submitted":
      current = villagePending ? 1 : review;
      break;
    case "in_review":
    case "needs_changes":
      current = review;
      break;
    case "approved":
      current = sign;
      break;
    case "signed":
      current = sign + 1;
      break;
    case "issued":
      current = labels.length;
      break;
    case "rejected":
      current = review;
      stopped = true;
      break;
    case "cancelled": {
      // ยกเลิกจากสถานะไหน — ดูจากประวัติแถวที่เข้า cancelled
      const from = req.history.find((h) => h.to_status === "cancelled")?.from_status;
      current = !from || from === "draft" ? 0 : from === "submitted" ? (villagePending ? 1 : review) : review;
      stopped = true;
      break;
    }
  }
  return labels.map((label, i) => ({
    label,
    state: i < current ? "done" : i === current ? (stopped ? "stopped" : "current") : "todo",
  }));
}

/** ข้อความในการ์ด ການດຳເນີນການ เมื่อผู้เปิดไม่มีปุ่มให้กด */
function waitingText(req: DocumentRequestDetail, villagePending: boolean): string {
  switch (req.status) {
    case "draft":
      return "ຜູ້ຂໍຍັງບໍ່ໄດ້ຍື່ນຄຳຮ້ອງ";
    case "submitted":
      return villagePending ? "ລໍນາຍບ້ານຢັ້ງຢືນ — ກຳນົດ 2 ວັນລັດຖະການ" : "ລໍຜູ້ກວດເອກະສານ ຮັບ ແລະ ກວດຄວາມຄົບຖ້ວນ";
    case "in_review":
      return req.assigned_officer_name
        ? `ລໍຜູ້ກວດເອກະສານ ກວດຄວາມຄົບຖ້ວນ (${req.assigned_officer_name})`
        : "ລໍຜູ້ກວດເອກະສານ ກວດຄວາມຄົບຖ້ວນ";
    case "needs_changes":
      return "ສົ່ງຄືນໃຫ້ແກ້ໄຂແລ້ວ — ລໍຖ້າຜູ້ຂໍແກ້ໄຂແລ້ວຍື່ນຄືນ";
    case "approved":
    case "signed":
      return "ຜ່ານການກວດແລ້ວ — ລໍຫົວໜ້າຕາແສງ ລົງນາມ ແລະ ປະທັບຕາ";
    case "issued":
      return "ອອກເອກະສານແລ້ວ";
    case "rejected":
      return "ຄຳຮ້ອງຖືກປະຕິເສດ";
    case "cancelled":
      return "ຜູ້ຂໍຍົກເລີກຄຳຮ້ອງ";
  }
}

const PAYMENT_METHOD_LO: Record<string, string> = { bcel: "BCEL", ldb: "LDB", jdb: "JDB", apb: "APB" };

/**
 * ค่าธรรมเนียม — ยอดจริงมาจากแถวชำระของคำร้อง (`GET /v1/requests/{id}/payment`, ตรวจ canRead ที่ server)
 * ยังไม่ชำระ = แสดงอัตราปัจจุบันของชนิดเอกสาร (อาจไม่ใช่อัตราตอนยื่น — ป้ายบอก)
 */
function FeeCard({ requestId, type }: { requestId: string; type: DocumentType | undefined }) {
  const payment = useQuery({
    queryKey: ["requests", "payment", requestId],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/v1/requests/{id}/payment", {
        params: { path: { id: requestId } },
        headers: authHeaders(),
      });
      if (error) throw new Error(describeError(error, response.status));
      return data ?? null;
    },
  });
  const p = payment.data;
  const rate = type?.fee_lak ?? null;

  return (
    <section className="card">
      <h2 className="section-title">ຄ່າທຳນຽມ</h2>
      {payment.isPending ? (
        <p className="caption" style={{ marginTop: "var(--space-3)" }}>
          ກຳລັງໂຫຼດ…
        </p>
      ) : payment.isError ? (
        <p className="caption" style={{ marginTop: "var(--space-3)" }}>
          ໂຫຼດຂໍ້ມູນການຊຳລະບໍ່ໄດ້: {payment.error.message}
        </p>
      ) : p ? (
        <>
          <dl className="rd-kv rd-kv--money">
            <dt>
              ອັດຕາ{type?.code ? ` (${type.code})` : ""}
            </dt>
            <dd className="num">{fullLAK(p.copies > 0 ? Math.round(p.amount_lak / p.copies) : p.amount_lak)} / ສະບັບ</dd>
            <dt>ຈຳນວນ</dt>
            <dd className="num">× {num(p.copies)}</dd>
            {p.stamp_lak > 0 ? (
              <>
                <dt>ອາກອນສະແຕັມ</dt>
                <dd className="num">{fullLAK(p.stamp_lak)}</dd>
              </>
            ) : null}
            <dt className="rd-kv__total">ລວມທີ່ຊຳລະ</dt>
            <dd className="num rd-kv__total">{fullLAK(p.total_lak)}</dd>
          </dl>
          <p className="caption" style={{ marginTop: "var(--space-2)" }}>
            ຊຳລະແລ້ວຜ່ານ {PAYMENT_METHOD_LO[p.method] ?? p.method} ·{" "}
            <span className="num">{dateTimeLao(new Date(p.paid_at).getTime())}</span>
          </p>
        </>
      ) : (
        <>
          <dl className="rd-kv rd-kv--money">
            <dt>
              ອັດຕາປັດຈຸບັນ{type?.code ? ` (${type.code})` : ""}
            </dt>
            <dd className="num">{rate == null ? "ບໍ່ມີຄ່າທຳນຽມ" : `${fullLAK(rate)} / ສະບັບ`}</dd>
          </dl>
          {rate != null ? (
            <p className="caption" style={{ marginTop: "var(--space-2)" }}>
              <span className="status-badge status-badge--pending">ຍັງບໍ່ຊຳລະ</span>
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * ขั้นรับรองของนายบ้าน (ADR-0031) — แสดงเมื่อ `village_certification` ไม่ null
 *
 * รูปลายเซ็นนายบ้าน (`signature_attachment_id`) เปิดผ่าน `/v1/attachments/{id}/url` เฉพาะผู้เห็นข้อมูลเต็ม
 * (การเงิน/กวดกาโดน 403 อยู่แล้ว ไม่ยิงให้เสียเปล่า)
 */
function VillageCertCard({
  cert,
  pending,
  overdue,
  showSignature,
  canWaive,
  onWaive,
}: {
  cert: VillageCertification;
  pending: boolean;
  overdue: boolean;
  showSignature: boolean;
  canWaive: boolean;
  onWaive: () => void;
}) {
  const sigId = showSignature ? cert.signature_attachment_id : null;
  const signature = useQuery({
    queryKey: ["attachments", "url", sigId],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/v1/attachments/{id}/url", {
        params: { path: { id: sigId! } },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data.url;
    },
    enabled: !!sigId,
    // presigned URL หมดอายุได้ — ไม่ cache ข้ามการเปิดหน้า
    staleTime: 0,
    gcTime: 0,
  });

  const row = (label: string, value: ReactNode) => (
    <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
      <span className="body-sm" style={{ color: "var(--gray-500)" }}>
        {label}
      </span>
      <span className="body-sm" style={{ fontWeight: 500, textAlign: "right" }}>
        {value}
      </span>
    </div>
  );

  return (
    <section className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 className="section-title">ນາຍບ້ານຢັ້ງຢືນ</h2>
        <span className={villageCertBadgeClass(cert.status)}>{VILLAGE_CERT_LABEL_LO[cert.status]}</span>
      </div>
      <div className="stack" style={{ marginTop: "var(--space-4)" }}>
        {pending && cert.due_at
          ? row(
              "ກຳນົດຂອງນາຍບ້ານ",
              <>
                <span className="num">{dateTimeLao(new Date(cert.due_at).getTime())}</span>
                {overdue ? (
                  <>
                    <br />
                    <span className="caption">ເກີນກຳນົດແລ້ວ — ຕາແສງຮັບໄດ້ ລະບົບຂ້າມຂັ້ນໃຫ້</span>
                  </>
                ) : null}
              </>,
            )
          : null}
        {cert.by_name ? row("ນາຍບ້ານ", cert.by_name) : null}
        {!pending && cert.at ? row("ວັນທີ", <span className="num">{dateTimeLao(new Date(cert.at).getTime())}</span>) : null}
        {cert.note ? row(cert.status === "waived" ? "ເຫດຜົນທີ່ຂ້າມຂັ້ນ" : "ໝາຍເຫດຂອງນາຍບ້ານ", cert.note) : null}
        {sigId
          ? row(
              "ລາຍເຊັນນາຍບ້ານ",
              signature.data ? (
                <img src={signature.data} alt="ລາຍເຊັນນາຍບ້ານ" style={{ maxHeight: "4rem", maxWidth: "12rem" }} />
              ) : signature.isError ? (
                <span className="caption">ໂຫຼດຮູບບໍ່ໄດ້</span>
              ) : (
                <span className="caption">ກຳລັງໂຫຼດ…</span>
              ),
            )
          : null}
        {cert.status === "waived" ? (
          <p className="caption">ຂ້າມຂັ້ນແລ້ວ — ເອກະສານທີ່ອອກຈະບໍ່ມີລາຍເຊັນນາຍບ້ານ</p>
        ) : null}
        {pending ? (
          <p className="caption">
            ການຢັ້ງຢືນຂອງນາຍບ້ານບໍ່ແມ່ນການອະນຸມັດ — ຕາແສງຍັງກວດ ແລະ ຕັດສິນຕາມປົກກະຕິ · ກຳນົດ 2 ວັນເຮັດການນັບແຕ່ຍື່ນ
          </p>
        ) : null}
        {canWaive && !overdue ? (
          <div className="row">
            <button type="button" className="btn-outline" onClick={onWaive}>
              ຂ້າມຂັ້ນນາຍບ້ານ
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function BackLink({ toSign = false }: { toSign?: boolean }) {
  return (
    <Link to={toSign ? "/requests/sign" : "/requests"} className="btn-outline" style={{ display: "inline-flex" }}>
      <ChevronLeft size={14} /> {toSign ? "ກັບລໍເຊັນ" : "ກັບຄິວຄຳຮ້ອງ"}
    </Link>
  );
}
